import { createFileRoute, Link } from "@tanstack/react-router";
import { routeMeta, leafCanonicalLink } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { CollectionFlagship, type FlagshipContent } from "@/components/CollectionFlagship";
import { getCollection } from "@/data/collections";
import heatingImg from "@/assets/hill-country/patio-infrared-heating.jpg";
import { MISTING_PHOTOS } from "@/data/mistingPhotos";
import lestalkImg from "@/assets/hill-country-luxury-pergola-pool.png.asset.json";
import coversImg from "@/assets/hill-country/patio-furniture-tv-covers.jpg";
import privacyImg from "@/assets/hill-country/sound-wall-privacy-panels.jpg";

const collection = getCollection("hill-country-series")!;

function LuxuryBackyardSystems() {
  return (
    <section className="bg-white py-16">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <div className="font-display text-xs uppercase tracking-[0.3em] text-red">
          ★ Luxury Backyard Systems
        </div>
        <h2 className="mt-2 font-display text-3xl font-bold text-navy-deep md:text-4xl">
          Heat it. Cool it. Light it. Use the backyard all year.
        </h2>
        <p className="mt-4 max-w-3xl text-base text-muted-foreground">
          Shade is only half of a high-end backyard. The Hill Country Series adds the comfort systems
          that make an outdoor room usable in August and in January — infrared patio heating,
          high-pressure misting and outdoor cooling, big-air fans, and integrated lighting, all
          designed into the awning, pergola, or sail shade instead of bolted on as an afterthought.
        </p>

        <div className="mt-10 grid gap-8 md:grid-cols-2">
          <article className="overflow-hidden border-l-4 border-red bg-cream shadow-card">
            <img
              src={heatingImg}
              alt="Infrared patio heater mounted under a pergola over a luxury outdoor kitchen at dusk"
              loading="lazy"
              width={1280}
              height={853}
              className="h-56 w-full object-cover md:h-64"
            />
            <div className="p-6">
              <h3 className="font-display text-xl font-bold text-navy-deep">
                Outdoor Heating Systems
              </h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Flush-mount and ceiling-hung infrared heaters (typically 1,500W–3,000W, multi-level
                heat, remote and smart-controlled) that warm people and surfaces instead of blowing
                hot air around. We size the run to your patio, hide the wiring in the frame, and tie
                it into the same control as your retractable and lighting.
              </p>
              <ul className="mt-4 space-y-1 text-sm text-navy-deep">
                <li>• Infrared electric heaters — no flame, no propane tanks</li>
                <li>• Mounted into awning, pergola and patio-cover framing</li>
                <li>• Remote, timer and smart-home compatible</li>
                <li>• Weather-rated for covered Texas patios</li>
              </ul>
            </div>
          </article>

          <article className="overflow-hidden border-l-4 border-red bg-cream shadow-card">
            <img
              src={MISTING_PHOTOS[0].src}
              alt={MISTING_PHOTOS[0].alt}
              loading="lazy"
              width={1280}
              height={853}
              className="h-56 w-full object-cover md:h-64"
            />
            <div className="p-6">
              <h3 className="font-display text-xl font-bold text-navy-deep">
                Outdoor Misting Cooling — Retail Aeromist Dealer
              </h3>
              <p className="mt-2 text-sm text-muted-foreground">
                We are a retail dealer for Aeromist, and because of Texas humidity we build{" "}
                <strong>fan misting systems</strong> — a high-pressure nozzle set in front of an
                outdoor-rated fan, so the fog is atomized and carried across the seating area
                instead of hanging in humid air. Add a motorized drop screen and a west-facing patio
                becomes the coolest seat in the house.
              </p>
              <ul className="mt-4 space-y-1 text-sm text-navy-deep">
                <li>• Aeromist oscillating, wall, post &amp; pedestal misting fans</li>
                <li>• High-pressure pumps, stainless nozzles &amp; hidden tubing</li>
                <li>• Motorized drop screens to hold the cool air in</li>
                <li>• Retrofits onto most sound existing structures</li>
              </ul>
              <Link
                to="/outdoor-cooling-misting"
                className="mt-4 inline-flex items-center font-display text-sm font-extrabold uppercase tracking-wider text-red underline decoration-2 underline-offset-4 hover:text-red-deep"
              >
                See Outdoor Misting Cooling →
              </Link>
            </div>
          </article>
        </div>

        <h3 className="mt-12 font-display text-2xl font-bold text-navy-deep">
          Misting &amp; outdoor cooling we&apos;ve installed
        </h3>
        <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {MISTING_PHOTOS.slice(1, 7).map((photo) => (
            <figure key={photo.src} className="overflow-hidden bg-cream shadow-card">
              <img
                src={photo.src}
                alt={photo.alt}
                loading="lazy"
                width={1280}
                height={853}
                className="h-48 w-full object-cover"
              />
              <figcaption className="p-4">
                <span className="font-display text-xs font-extrabold uppercase tracking-wider text-red">
                  {photo.label}
                </span>
                <p className="mt-1 text-sm text-muted-foreground">{photo.caption}</p>
              </figcaption>
            </figure>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/outdoor-cooling-misting"
            className="inline-flex items-center bg-navy-deep px-6 py-3 font-display text-sm font-extrabold uppercase tracking-wider text-white transition hover:bg-navy"
          >
            Outdoor Misting Cooling
          </Link>
          <Link
            to="/custom-fabrication"
            className="inline-flex items-center bg-red px-6 py-3 font-display text-sm font-extrabold uppercase tracking-wider text-white transition hover:bg-red-deep"
          >
            Design My Backyard System
          </Link>
          <Link
            to="/exterior-vertical-shades"
            className="inline-flex items-center border-2 border-navy-deep px-6 py-3 font-display text-sm font-extrabold uppercase tracking-wider text-navy-deep transition hover:bg-navy-deep hover:text-white"
          >
            Add Motorized Drop Screens
          </Link>
        </div>
      </div>
    </section>
  );
}

function CoversAndPrivacy() {
  return (
    <section className="bg-cream py-16">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <div className="font-display text-xs uppercase tracking-[0.3em] text-red">
          ★ Protect It & Screen It
        </div>
        <h2 className="mt-2 font-display text-3xl font-bold text-navy-deep md:text-4xl">
          Custom covers, sound walls & privacy panels
        </h2>

        <div className="mt-8 grid items-start gap-8 md:grid-cols-2">
          <div>
            <img
              src={coversImg}
              alt="Patio chairs under fitted custom covers with an outdoor TV protected by a weatherproof cover"
              loading="lazy"
              width={1280}
              height={853}
              className="w-full rounded-sm object-cover shadow-card"
            />
            <h3 className="mt-5 font-display text-xl font-bold text-navy-deep">
              Custom Outdoor Covers
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">
              You spent real money on the backyard — we keep the sun, hail, dust and hard freezes off
              of it. Every cover is patterned to your exact piece and sewn in Sunbrella® or marine
              vinyl in our Seguin shop, with tie-downs, zippers and vents where they belong.
            </p>
            <ul className="mt-4 grid gap-1 text-sm text-navy-deep sm:grid-cols-2">
              <li>• Outdoor TV covers</li>
              <li>• Patio furniture & sectional covers</li>
              <li>• Outdoor speaker & A/V covers</li>
              <li>• Plant & landscape freeze covers</li>
              <li>• Grill, smoker & island covers</li>
              <li>• Fire pit, heater & fan covers</li>
            </ul>
          </div>

          <div>
            <img
              src={privacyImg}
              alt="Backyard sound wall and privacy panels screening a luxury pool patio from neighbors"
              loading="lazy"
              width={1280}
              height={853}
              className="w-full rounded-sm object-cover shadow-card"
            />
            <h3 className="mt-5 font-display text-xl font-bold text-navy-deep">
              Sound Walls & Privacy Panels
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Block the neighbor's view, knock down road and pool-equipment noise, and close in an
              open patio without building a wall. Welded aluminum frames with dense acoustic-grade or
              Commercial 95 panels — fixed, sliding or removable for the season.
            </p>
            <ul className="mt-4 grid gap-1 text-sm text-navy-deep sm:grid-cols-2">
              <li>• Acoustic sound-dampening walls</li>
              <li>• Hot tub & pool privacy panels</li>
              <li>• Equipment & A/C screen walls</li>
              <li>• Property-line screening</li>
              <li>• Fixed, sliding or removable</li>
              <li>• Color-matched to your shade</li>
            </ul>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/covers-tops"
            className="inline-flex items-center bg-red px-6 py-3 font-display text-sm font-extrabold uppercase tracking-wider text-white transition hover:bg-red-deep"
          >
            Quote My Custom Covers
          </Link>
          <Link
            to="/gazebo-privacy-panels"
            className="inline-flex items-center border-2 border-navy-deep px-6 py-3 font-display text-sm font-extrabold uppercase tracking-wider text-navy-deep transition hover:bg-navy-deep hover:text-white"
          >
            See Privacy Panels
          </Link>
        </div>
      </div>
    </section>
  );
}

const content: FlagshipContent = {
  formImageSrc: lestalkImg.url,
  formImageAlt: "Luxury black-frame pergola with turquoise shade fabric over a poolside patio lounge",

  eyebrow: "Signature Collection · Outdoor Living & Premium Shade",
  heroKicker: "Luxury Backyards · Boerne · Fredericksburg · Lake Austin · Wimberley · San Antonio",
  lifestyleIntro:
    "This is the best-backyard-in-Texas division — luxury retractables and the finish details that turn a patio into an outdoor room you never want to leave.",
  lifestyleBody:
    "The Hill Country Series is Texas Made Shade's luxury outdoor backyard living line. Motorized Solair® retractable awnings, custom pergola covers, backyard upholstery and cushions sewn in our shop, outdoor kitchen and grill covers, poolside umbrellas and cabana shade — then the extras most shade companies never touch: integrated outdoor lighting for awnings, pergolas and sail shades, high-pressure misting systems, and patio heating so the season never ends. Welded, sewn, wired and installed by our own crew out of Seguin, TX.",
  solutionsHeading: "Everything that makes a luxury backyard",
  solutions: [
    { title: "Luxury Retractable Awnings", desc: "Motorized Solair® systems sized for wide estate patios.", to: "/retractable-awnings" },
    { title: "Backyard Upholstery", desc: "Custom outdoor cushions, seating and daybed upholstery in Sunbrella®.", to: "/covers-tops" },
    { title: "Pergola Covers", desc: "Fixed, retractable and louvered covers built to your architect's spec.", to: "/pergola-covers" },
    { title: "Outdoor Kitchen Covers", desc: "Grill, smoker, island and outdoor-kitchen covers cut to your build.", to: "/covers-tops" },
    { title: "Poolside Umbrellas & Cabanas", desc: "Resort-grade umbrellas, cabana tops and pool deck shade.", to: "/sail-shades" },
    { title: "Outdoor Lighting — Bosso Smart Lighting", desc: "Authorized Bosso dealer: patio, pergola, awning, pool and landscape lighting with app control.", to: "/outdoor-lighting" },
    { title: "Smart Lighting & Automation", desc: "Lone Star Series™ — lighting, motors, screens, sensors and scenes on one system.", to: "/lone-star-elite" },
    { title: "Patio Curtains & Drop Curtains", desc: "Outdoor curtains and drop curtains for shade, privacy and wind.", to: "/curtains" },
    { title: "Outdoor Misting Cooling — Aeromist", desc: "Retail Aeromist dealer: fan misting systems built for Texas humidity.", to: "/outdoor-cooling-misting" },
    { title: "Outdoor Heating & Cooling", desc: "Infrared patio heat, cooling equipment and conditioned outdoor rooms.", to: "/outdoor-hvac" },
    { title: "Outdoor Cooling & Misting Fans", desc: "Oscillating and pedestal misting fans for real air movement.", to: "/outdoor-cooling-misting" },
    { title: "Exterior Vertical Shades", desc: "Motorized drop screens for wraparound porches and outdoor rooms.", to: "/exterior-vertical-shades" },
    { title: "Estate Sail Shades", desc: "Tensioned sails engineered for Hill Country wind exposure.", to: "/sail-shades" },
    { title: "Custom Fabrication", desc: "One-off welded structures for unique estate builds.", to: "/custom-fabrication" },
    { title: "Ranger Care™", desc: "Annual cleaning, re-tension and inspection for luxury systems.", to: "/ranger-care" },
    { title: "Outdoor TV & Speaker Covers", desc: "Fitted weatherproof covers for outdoor TVs, A/V and speakers.", to: "/covers-tops" },
    { title: "Patio Furniture & Plant Covers", desc: "Custom furniture, sectional and freeze-protection plant covers.", to: "/covers-tops" },
    { title: "Gazebo Covers & Privacy Panels", desc: "Custom pergola and gazebo tops plus privacy panels for pools, patios and hot tubs.", to: "/gazebo-privacy-panels" },
  ],
  afterSolutions: (
    <>
      <LuxuryBackyardSystems />
      <CoversAndPrivacy />
    </>
  ),


  trustBadges: [
    "Luxury retractable specialists",
    "Sunbrella® upholstery sewn in-house",
    "Solair® Authorized Dealer",
    "Lighting · Misting · Heat integrated",
  ],
  faqs: [
    {
      q: "What makes the Hill Country Series different?",
      a: "It's our luxury tier. Bigger retractables, custom upholstery, and the integrated extras — lighting, misting and heating — that turn shade into a finished outdoor living room.",
    },
    {
      q: "Do you really do backyard upholstery?",
      a: "Yes. We sew outdoor cushions, seat pads, daybeds and replacement covers in Sunbrella® right alongside the awnings, so fabrics and colors match across the whole backyard.",
    },
    {
      q: "What kind of outdoor heaters do you install?",
      a: "Electric infrared heaters — typically 1,500W to 3,000W with multiple heat levels and remote or smart control — mounted into the awning, pergola or patio-cover frame. Infrared warms people and surfaces directly, so it works even on a breezy Hill Country evening, with no propane tanks to swap.",
    },
    {
      q: "How well does misting and outdoor cooling actually work in Texas?",
      a: "A properly designed high-pressure misting system drops patio temps 20–30°F with a fog so fine nothing gets wet. Paired with outdoor fans and a motorized drop screen on the sun side, a west-facing patio stays usable through a Texas afternoon.",
    },
    {
      q: "Can you add lighting, misting or heat to an existing awning or pergola?",

      a: "Usually yes. We can retrofit lighting strips, misting lines and heaters into most sound awning, pergola and sail shade frames — and we'll tell you honestly if a frame isn't a good candidate.",
    },
    {
      q: "Do you work with our architect or designer?",
      a: "Yes — send drawings, elevations, or an inspiration board and we'll build to spec, including 3D renderings for HOA or design review.",
    },
    {
      q: "Where do you install?",
      a: "Boerne, Fredericksburg, Kerrville, Wimberley, Dripping Springs, Lake Travis, San Antonio, Austin and surrounding areas throughout the Hill Country — and we travel all around Texas, including Houston, Dallas–Fort Worth, Corpus Christi, the Coastal Bend and the Rio Grande Valley — from our Seguin, TX shop.",
    },
  ],
};

export const Route = createFileRoute("/hill-country-series")({
  head: (ctx) => ({
    meta: routeMeta(ctx, {
      title: "Hill Country Series™ — Outdoor Living & Premium Shade | Texas Made Shade",
      description: collection.description,
      keywords:
        "luxury retractable awnings texas, retractable patio awning, deck awnings texas, awnings for decks, awnings near me, patio awnings hill country, outdoor backyard living san antonio, backyard upholstery, outdoor kitchen covers, poolside umbrellas, outdoor lighting pergola, misting systems texas, patio heating systems, outdoor tv covers, patio furniture covers, outdoor speaker covers, plant freeze covers, backyard sound wall, privacy panels texas, infrared patio heaters, outdoor heating systems texas, outdoor cooling systems, outdoor misting fans, boerne pergola, fredericksburg awnings",
    }),
    links: leafCanonicalLink(ctx, collection.path),
    scripts: serviceHeadScripts({
      path: collection.path,
      serviceName: "Hill Country Series Outdoor Living & Premium Shade",
      serviceType: "Luxury Outdoor Living, Retractables & Backyard Systems",
      description: collection.description,
    }),
  }),
  component: () => <CollectionFlagship collection={collection} content={content} />,
});
